import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { withStyles } from '@material-ui/core';

import Transition from '../../components/Dialogs/Transition';
import { add as addInventoryAdjustment } from '../../modules/inventory/inventoryAdjustment';

const styles = () => ({
	dialogPaper: {
		maxWidth: '100%',
		width: 480,
	},
	dialogPadding: {
		padding: '10px 25px 20px 25px',
	},
	subTitle: {
		fontSize: '0.85rem',
		color: '#666',
		marginTop: -14,
		marginBottom: 10,
	},
	breakdown: {
		fontFamily: 'monospace',
		fontSize: '0.9rem',
		backgroundColor: '#fafafa',
		border: '1px solid #eee',
		borderRadius: 4,
		padding: '10px 14px',
		marginBottom: 16,
	},
	breakdownRow: {
		display: 'flex',
		justifyContent: 'space-between',
		padding: '1px 0',
	},
	breakdownTotal: {
		display: 'flex',
		justifyContent: 'space-between',
		borderTop: '1px solid #ccc',
		marginTop: 4,
		paddingTop: 4,
		fontWeight: 700,
	},
	newEnding: {
		display: 'flex',
		justifyContent: 'space-between',
		fontWeight: 700,
		marginTop: 2,
	},
	pos: { color: '#2e7d32' },
	neg: { color: '#c62828' },
	rolloverNote: {
		fontSize: '0.78rem',
		color: '#888',
		marginTop: 12,
	},
	errorText: {
		color: '#c62828',
		fontSize: '0.85rem',
		marginTop: 8,
	},
});

const fmtGal = (n) => {
	if (n == null) {
		return '-';
	}
	const abs = Math.round(Math.abs(n));
	const fmt = new Intl.NumberFormat('en-US').format(abs);
	return n < 0 ? `(${fmt})` : fmt;
};

/**
 * Add a manual gallons adjustment to one terminal/supplier/product group. Shows the
 * group's gallons breakdown, takes signed gallons + a required reason, and live-previews
 * the new ending inventory as the user types.
 */
class InventoryAdjustmentDialog extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			gallonsText: '',
			reason: '',
			isSaving: false,
			error: null,
		};
	}

	componentDidUpdate(prevProps) {
		//- Reset the form each time the dialog opens for a (possibly different) group.
		if (this.props.open && !prevProps.open) {
			this.setState({ gallonsText: '', reason: '', isSaving: false, error: null });
		}
	}

	parseGallons = () => {
		const trimmed = _.trim(this.state.gallonsText);
		if (!/^-?\d+$/.test(trimmed)) {
			return null;
		}
		const parsed = parseInt(trimmed, 10);
		return parsed === 0 ? null : parsed;
	};

	handleSave = () => {
		const { group, periodYearMonth, onSaved } = this.props;
		const adjustmentGallons = this.parseGallons();
		const reason = _.trim(this.state.reason);

		if (adjustmentGallons == null) {
			return this.setState({ error: 'Gallons must be a non-zero whole number (negative allowed)' });
		}
		if (!reason) {
			return this.setState({ error: 'A reason for the adjustment is required' });
		}

		const period = moment(periodYearMonth, 'YYYY-MM');
		this.setState({ isSaving: true, error: null });
		addInventoryAdjustment({
			terminalId: group.terminalId,
			supplierForTerminalId: group.supplierForTerminalId || 0,
			productType: group.productType,
			periodYear: period.year(),
			periodMonth: period.month() + 1,
			adjustmentGallons,
			reason,
			callback: (err) => {
				if (err) {
					const errors = _.isArray(err) ? err : [err];
					return this.setState({
						isSaving: false,
						error: errors.map((e) => (e && e.message) || String(e)).join(', '),
					});
				}
				this.setState({ isSaving: false });
				onSaved();
			},
		});
	};

	render() {
		const { classes, open, onClose, group, groupTitle, periodYearMonth } = this.props;
		const { gallonsText, reason, isSaving, error } = this.state;

		if (!group) {
			return null;
		}

		const {
			beginningInventoryGallons,
			purchasedGallons,
			soldGallons,
			gainLossForTimeFrame,
			adjustments = [],
			endingInventoryGallons,
		} = group;

		const enteredGallons = this.parseGallons();
		const newEnding =
			enteredGallons == null ? null : (endingInventoryGallons || 0) + enteredGallons;
		const monthName = moment(periodYearMonth, 'YYYY-MM').format('MMMM YYYY');

		const galClass = (n) => (n < 0 ? classes.neg : n > 0 ? classes.pos : undefined);

		const rows = [
			{ label: 'Beg. Inventory', gallons: beginningInventoryGallons },
			{ label: 'Purchased', gallons: purchasedGallons },
			{ label: 'Sold', gallons: -soldGallons },
			{ label: 'Gain / Loss', gallons: gainLossForTimeFrame },
			...adjustments.map((adjustment, i) => ({
				label: `Adjust #${i + 1}`,
				gallons: adjustment.adjustmentGallons,
			})),
		];

		return (
			<Dialog
				classes={{ paper: classes.dialogPaper }}
				open={open}
				TransitionComponent={Transition}
				onClose={isSaving ? undefined : onClose}
			>
				<div className={classes.dialogPadding}>
					<DialogTitle>Add Inventory Adjustment</DialogTitle>
					<DialogContent>
						<div className={classes.subTitle}>
							{groupTitle} — {monthName}
						</div>

						<div className={classes.breakdown}>
							{rows.map((row) => (
								<div key={row.label} className={classes.breakdownRow}>
									<span>{row.label}:</span>
									<span className={galClass(row.gallons)}>{fmtGal(row.gallons)}</span>
								</div>
							))}
							<div className={classes.breakdownTotal}>
								<span>End. Inventory:</span>
								<span className={galClass(endingInventoryGallons)}>
									{fmtGal(endingInventoryGallons)}
								</span>
							</div>
							{enteredGallons != null && (
								<React.Fragment>
									<div className={classes.breakdownRow}>
										<span>This adjustment:</span>
										<span className={galClass(enteredGallons)}>{fmtGal(enteredGallons)}</span>
									</div>
									<div className={classes.newEnding}>
										<span>New End. Inventory:</span>
										<span className={galClass(newEnding)}>{fmtGal(newEnding)}</span>
									</div>
								</React.Fragment>
							)}
						</div>

						<TextField
							label="Adjustment Gallons"
							value={gallonsText}
							fullWidth
							required
							helperText="Whole gallons. Use a negative number to reduce inventory (eg -1500)."
							onChange={(e) => this.setState({ gallonsText: e.target.value, error: null })}
						/>
						<TextField
							label="Reason"
							value={reason}
							fullWidth
							required
							multiline
							rows={3}
							style={{ marginTop: 12 }}
							helperText="Required. Why is this adjustment needed?"
							onChange={(e) => this.setState({ reason: e.target.value, error: null })}
						/>

						{error && <div className={classes.errorText}>{error}</div>}

						<div className={classes.rolloverNote}>
							The new ending inventory rolls forward as{' '}
							{moment(periodYearMonth, 'YYYY-MM').add(1, 'month').format('MMMM YYYY')}'s
							beginning inventory (the persisted balance updates within a few minutes).
						</div>
					</DialogContent>
					<DialogActions>
						<Button onClick={onClose} disabled={isSaving}>
							Cancel
						</Button>
						<Button
							color="primary"
							variant="contained"
							onClick={this.handleSave}
							disabled={isSaving || enteredGallons == null || !_.trim(reason)}
						>
							{isSaving ? <CircularProgress size={20} /> : 'Save Adjustment'}
						</Button>
					</DialogActions>
				</div>
			</Dialog>
		);
	}
}

InventoryAdjustmentDialog.propTypes = {
	classes: PropTypes.object.isRequired,
	open: PropTypes.bool.isRequired,
	group: PropTypes.object,
	groupTitle: PropTypes.string,
	periodYearMonth: PropTypes.string,
	onClose: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
};

export default withStyles(styles)(InventoryAdjustmentDialog);
